import type { DynamicComponentProps } from '@/components/form-builder/registry';
import { FF_IMMEDIATE_NURSE_CONSULTATION } from '@/constants/featureFlags';
import { GoogleAnalyticsEventName } from '@/constants/types';
import type { PreScreeningData } from '@/context/pre-screening-context';
import { usePrescreeningData } from '@/context/pre-screening-context';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import useGoogleTagManager from '@/hooks/useGoogleTagManager';
import {
  ImmediateNurseConsultationVariant,
  useImmediateNurseConsultVariant,
} from '@/hooks/useImmediateNurseConsultVariant';
import { AddDiscountCode } from '@/pages/components/AddDiscountCode';
import { CtaButton, CtaSection } from '@/pages/components/styled/Cta';
import { formatStringToSha256Hash } from '@/utils/ga-hashing';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { Box, CircularProgress, Stack, styled, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const LoadingContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '200px',
}));

export const EligibilityVariantImmediateConsult = ({ onNextStep, handleShowCalendar }: DynamicComponentProps) => {
  const { flags, loading } = useFeatureFlags();
  const isImmediateNurseConsultationEnabled = flags[FF_IMMEDIATE_NURSE_CONSULTATION];
  const immediateConsultVariant = useImmediateNurseConsultVariant();

  const { setPrescreeningData } = usePrescreeningData();
  setPrescreeningData((current: PreScreeningData) => {
    return {
      ...current,
      immediateNurseConsultation: {
        variant: immediateConsultVariant,
      },
    };
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { sendGoogleAnalyticsEvent } = useGoogleTagManager();

  const [searchParams] = useSearchParams();
  const promocode = searchParams.get('promocode') || '';

  const userEmail = searchParams.get('email') || localStorage.getItem('prescreeningUserEmail');
  const shaEmail = userEmail ? formatStringToSha256Hash(userEmail) : '';

  const handleAnalyticsOnClick = () => {
    const trackingPayload = {
      event_category: 'cal.com',
      event_label: 'Immediate Nurse Consultation',
      sha_email: shaEmail,
      ...(promocode ? { promocode } : {}),
    };
    sendGoogleAnalyticsEvent(GoogleAnalyticsEventName.START_BOOKING, trackingPayload);
  };

  const showScheduleForLaterButton: boolean = [
    ImmediateNurseConsultationVariant.VARIANT,
    ImmediateNurseConsultationVariant.VARIANT_2,
    ImmediateNurseConsultationVariant.VARIANT_AMAZON_CONNECT_INITIATED_INC,
  ].includes(immediateConsultVariant);

  useEffect(() => {
    // GA4 experiment impression
    if (isImmediateNurseConsultationEnabled && !loading) {
      const trackingPayload = {
        experiment_id: FF_IMMEDIATE_NURSE_CONSULTATION,
        variant_id: immediateConsultVariant,
        experiment_platform: 'LaunchDarkly',
        sha_email: shaEmail,
        ...(promocode ? { promocode } : {}),
      };
      sendGoogleAnalyticsEvent(GoogleAnalyticsEventName.EXPERIMENT_IMPRESSION, trackingPayload);
    }
  }, [sendGoogleAnalyticsEvent, searchParams, immediateConsultVariant, shaEmail]);

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress color="primary" />
      </LoadingContainer>
    );
  }

  const speakToANurseNowText = 'Speak to a nurse now';
  const averageWaitTimeText = 'Avg. wait 2 - 5 mins';

  return (
    <Box data-testid="eligibility-variant">
      <Typography variant="h6" component="h2" fontWeight={'bold'} marginBottom={8}>
        {"You're eligible for a complimentary initial assessment"}
      </Typography>
      <Typography marginBottom={5}>
        Choose to speak to a nurse now about your medical history and our services, or schedule for later.
      </Typography>
      <Stack spacing={4}>
        <Typography>{"What you'll need:"}</Typography>
        <Stack flexDirection={'row'} alignItems={'center'}>
          <CreditCardIcon />
          <Typography marginLeft={2}>Medicare card number (or IHI)</Typography>
        </Stack>
        <Stack flexDirection={'row'} alignItems={'center'}>
          <CreditCardIcon />
          <Typography marginLeft={2}>Commonwealth concession card (if applicable)</Typography>
        </Stack>
      </Stack>
      <AddDiscountCode />
      <CtaSection>
        {isImmediateNurseConsultationEnabled &&
        immediateConsultVariant !== ImmediateNurseConsultationVariant.CONTROL ? (
          <>
            <CtaButton
              type="button"
              color="primary"
              size="large"
              variant="contained"
              onClick={() => {
                handleAnalyticsOnClick();
                onNextStep?.();
              }}
            >
              <Box display="flex" flexDirection="column" alignItems="center">
                {isMobile ? (
                  <>
                    <Typography fontWeight={'bold'}>{speakToANurseNowText}</Typography>
                    <Typography>{averageWaitTimeText}</Typography>
                  </>
                ) : (
                  <Typography>
                    <Box component="span" fontWeight="bold">
                      {speakToANurseNowText}
                    </Box>{' '}
                    ({averageWaitTimeText})
                  </Typography>
                )}
              </Box>
            </CtaButton>
            {showScheduleForLaterButton && (
              <>
                <Typography alignSelf="center">or</Typography>
                <CtaButton type="button" color="primary" size="large" variant="outlined" onClick={handleShowCalendar}>
                  Schedule for later
                </CtaButton>
              </>
            )}
          </>
        ) : (
          <CtaButton
            type="button"
            color="secondary"
            size="large"
            variant="contained"
            onClick={handleShowCalendar}
            startIcon={<CalendarTodayIcon />}
          >
            <Typography>Schedule</Typography>
          </CtaButton>
        )}
      </CtaSection>
    </Box>
  );
};
